import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import websiteSubscriptionService from '../../services/websiteSubscriptionService';
import './SubscriptionManagement.css';
import './SubscriptionManagement-Mobile.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './SubscriptionManagement.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SubscriptionCard = ({ subscription, onSubscribe, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  const handleSubscribe = () => {
    setShowCheckout(true);
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your website subscription? You will lose access to your website features.')) {
      return;
    }

    setLoading(true);
    try {
      await onCancel();
    } catch (error) {
      console.error('Error canceling subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { text: 'Active', class: 'status-active' },
      canceled: { text: 'Canceled', class: 'status-canceled' },
      past_due: { text: 'Past Due', class: 'status-past-due' },
      incomplete: { text: 'Incomplete', class: 'status-incomplete' }
    };
    
    const badge = badges[status] || { text: status, class: 'status-default' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  return (
    <div className="subscription-card">
      <div className="subscription-header">
        <div className="subscription-icon">🌐</div>
        <div className="subscription-info">
          <h3>Website Add-On</h3>
          <p>Create and manage your personalized agent website</p>
        </div>
        <div className="subscription-price">
          $4.99<span>/month</span>
        </div>
      </div>

      {subscription ? (
        <div className="subscription-details">
          <div className="subscription-status">
            <span>Status: </span>
            {getStatusBadge(subscription.status)}
          </div>
          
          {subscription.current_period_end && (
            <div className="subscription-period">
              <span>Next billing: </span>
              {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
            </div>
          )}

          <div className="subscription-actions">
            {subscription.status === 'active' ? (
              <button 
                className="cancel-subscription-button"
                onClick={handleCancel}
                disabled={loading}
              >
                {loading ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            ) : subscription.status === 'canceled' ? (
              <button 
                className="resubscribe-button"
                onClick={handleSubscribe}
                disabled={loading}
              >
                Resubscribe
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="subscription-not-active">
          <p>You don't have an active website subscription.</p>
          
          {!showCheckout ? (
            <button 
              className="subscribe-button"
              onClick={handleSubscribe}
              disabled={loading}
            >
              Subscribe Now
            </button>
          ) : (
            <Elements stripe={stripePromise}>
              <CheckoutForm 
                onSuccess={onSubscribe}
                onCancel={() => setShowCheckout(false)}
              />
            </Elements>
          )}
        </div>
      )}
    </div>
  );
};

const CheckoutForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) return;
    
    setLoading(true);
    setError('');

    const cardElement = elements.getElement(CardElement);

    try {
      const { error: paymentError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (paymentError) {
        setError(paymentError.message);
        setLoading(false);
        return;
      }

      const result = await websiteSubscriptionService.subscribe(paymentMethod.id);
      
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Subscription failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="card-element-container">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="checkout-actions">
        <button 
          type="button" 
          onClick={onCancel}
          className="cancel-button"
          disabled={loading}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          disabled={!stripe || loading}
          className="subscribe-button"
        >
          {loading ? 'Processing...' : 'Subscribe $4.99/month'}
        </button>
      </div>
    </form>
  );
};

const SubscriptionManagement = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const status = await websiteSubscriptionService.getSubscriptionStatus();
      setSubscription(status.subscription);
    } catch (err) {
      setError('Failed to load subscription status');
      console.error('Error loading subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only load subscription for agency agents
    if (user && user.role === 'agent' && user.agency_id) {
      loadSubscriptionStatus();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleSubscribe = async () => {
    await loadSubscriptionStatus();
  };

  const handleCancel = async () => {
    try {
      await websiteSubscriptionService.cancelSubscription();
      await loadSubscriptionStatus();
    } catch (err) {
      setError('Failed to cancel subscription');
      throw err;
    }
  };

  // Don't show subscription management for users who don't need it
  // Check for both agency_id and agencyId (different naming conventions)
  const hasAgencyId = user?.agency_id || user?.agencyId;
  
  if (!user || user.role !== 'agent' || !hasAgencyId) {
    return null;
  }

  if (loading) {
    return (
      <div className="subscription-loading">
        <div className="loading-spinner"></div>
        <p>Loading subscription information...</p>
      </div>
    );
  }

  return (
    <div className="subscription-management">
      <div className="subscription-management-header">
        <h2>My Plan</h2>
        <p>Manage your website add-on subscription</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <SubscriptionCard 
        subscription={subscription}
        onSubscribe={handleSubscribe}
        onCancel={handleCancel}
      />

      <div className="subscription-features">
        <h3>Website Add-On Features</h3>
        <div className="features-grid">
          <div className="feature-item">
            <span className="feature-icon">🎨</span>
            <div>
              <h4>Custom Branding</h4>
              <p>Personalize your website with custom themes and branding</p>
            </div>
          </div>
          <div className="feature-item">
            <span className="feature-icon">📝</span>
            <div>
              <h4>Content Management</h4>
              <p>Create and manage blog posts, promotions, and content</p>
            </div>
          </div>
          <div className="feature-item">
            <span className="feature-icon">📋</span>
            <div>
              <h4>Booking Forms</h4>
              <p>Client-facing forms for inquiries and bookings</p>
            </div>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🔍</span>
            <div>
              <h4>SEO Optimization</h4>
              <p>Search engine optimized pages for better visibility</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
